import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { 
  Search, BarChart3, TrendingUp, Users, Award, 
  Download, Filter, Trophy, Medal, ChevronUp, ChevronDown,
  FileSpreadsheet, FileText, Printer, Eye, Archive, RefreshCw, BookOpen
} from "lucide-react";
import AdminSidebar from "../components/AdminSidebar";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// User type labels for display
const USER_TYPE_LABELS = {
  "govt_secretariat": "Govt. Secretariat Staff",
  "govt_mts": "Govt. MTS Staff",
  "svep_mentor": "SVEP Mentor",
  "svep_crp_ep": "SVEP CRP-EPs",
  "svep_cep_coordinator": "SVEP CEP-EPs Coordinators",
  "staff_promotion": "Staff Promotion Exam",
  "vo_member": "VOs",
  "clf_member": "CLF",
  "shg_member": "SHGs",
  "other": "Others",
  // Legacy types
  "crp_ep": "CRP-EP",
  "mentor": "Mentor",
  "staff": "Staff"
};

export default function ReportsPage() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [blockFilter, setBlockFilter] = useState("all");
  const [qualificationFilter, setQualificationFilter] = useState("all"); // "all", "qualified", "not_qualified"
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailedSubmission, setDetailedSubmission] = useState(null);
  const [showScorecard, setShowScorecard] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [downloadingRecords, setDownloadingRecords] = useState(false);
  const [downloadingQnA, setDownloadingQnA] = useState(null); // holds block name while downloading
  const [qnaProgress, setQnaProgress] = useState({ current: 0, total: 0 });
  const printRef = useRef();

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchSubmissions();
  }, [navigate]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions`);
      setSubmissions(response.data);
    } catch (error) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissionDetails = async (submissionId) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/admin/submission/${submissionId}`);
      setDetailedSubmission(response.data);
      setShowScorecard(true);
    } catch (error) {
      toast.error("Failed to load submission details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Get unique states and blocks for filters
  const uniqueStates = useMemo(() => {
    const states = [...new Set(submissions.map(s => s.state).filter(Boolean))];
    return states.sort();
  }, [submissions]);

  const uniqueBlocks = useMemo(() => {
    let blocks = submissions.map(s => s.block).filter(Boolean);
    if (stateFilter !== "all") {
      blocks = submissions.filter(s => s.state === stateFilter).map(s => s.block).filter(Boolean);
    }
    return [...new Set(blocks)].sort();
  }, [submissions, stateFilter]);

  // Filter and sort submissions
  const filteredSubmissions = useMemo(() => {
    let filtered = [...submissions];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.candidate_name?.toLowerCase().includes(term) ||
        s.registration_number?.toLowerCase().includes(term)
      );
    }
    
    if (stateFilter !== "all") {
      filtered = filtered.filter(s => s.state === stateFilter);
    }
    
    if (blockFilter !== "all") {
      filtered = filtered.filter(s => s.block === blockFilter);
    }

    // Filter by qualification status
    if (qualificationFilter === "qualified") {
      filtered = filtered.filter(s => s.qualified === true);
    } else if (qualificationFilter === "not_qualified") {
      filtered = filtered.filter(s => s.qualified !== true);
    }
    
    filtered.sort((a, b) => {
      const scoreA = a.total_score || 0;
      const scoreB = b.total_score || 0;
      return sortOrder === "desc" ? scoreB - scoreA : scoreA - scoreB;
    });
    
    return filtered;
  }, [submissions, searchTerm, stateFilter, blockFilter, qualificationFilter, sortOrder]);

  // Qualified candidates count
  const qualifiedCount = useMemo(() => {
    return submissions.filter(s => s.qualified === true).length;
  }, [submissions]);

  const notQualifiedCount = useMemo(() => {
    return submissions.filter(s => s.qualified !== true).length;
  }, [submissions]);

  // Block-wise statistics
  const blockStats = useMemo(() => {
    const stats = {};
    submissions.forEach(s => {
      const block = s.block || "Unknown";
      if (!stats[block]) {
        stats[block] = {
          block,
          state: s.state || "Unknown",
          count: 0,
          totalScore: 0,
          maxScore: 0,
          minScore: Infinity,
          graded: 0
        };
      }
      stats[block].count++;
      stats[block].totalScore += (s.total_score || 0);
      stats[block].maxScore = Math.max(stats[block].maxScore, s.total_score || 0);
      stats[block].minScore = Math.min(stats[block].minScore, s.total_score || 0);
      if (s.status === "graded") stats[block].graded++;
    });
    
    return Object.values(stats).map(s => ({
      ...s,
      avgScore: s.count > 0 ? (s.totalScore / s.count).toFixed(1) : 0,
      minScore: s.minScore === Infinity ? 0 : s.minScore
    })).sort((a, b) => parseFloat(b.avgScore) - parseFloat(a.avgScore));
  }, [submissions]);

  // Overall statistics
  const overallStats = useMemo(() => {
    if (submissions.length === 0) return { total: 0, avgScore: 0, topScore: 0, graded: 0 };
    const scores = submissions.map(s => s.total_score || 0);
    return {
      total: submissions.length,
      avgScore: (scores.reduce((a, b) => a + b, 0) / submissions.length).toFixed(1),
      topScore: Math.max(...scores),
      graded: submissions.filter(s => s.status === "graded").length
    };
  }, [submissions]);

  // Export to Excel
  const exportToExcel = (data, filename) => {
    const exportData = data.map((s, index) => ({
      "Rank": index + 1,
      "Candidate Name": s.candidate_name,
      "Registration No": s.registration_number,
      "User Type": USER_TYPE_LABELS[s.user_type] || s.user_type || "CRP-EP",
      "State": s.state,
      "District": s.district,
      "Block": s.block,
      "Gram Panchayat": s.gram_panchayat || "-",
      "Section I (GK/Eng/Reasoning)": `${s.section1_score || 0}/${s.section1_total || 50}`,
      "Section II (Math)": `${s.section2_score || 0}/${s.section2_total || 40}`,
      "Section II Qualified": s.section2_qualified ? "Yes" : "No",
      "Section III (Subjective)": `${s.subjective_score}/${s.subjective_total || 10}`,
      "Total Score": `${s.total_score}/${s.max_score || 100}`,
      "Overall Qualified": s.qualified ? "QUALIFIED" : "NOT QUALIFIED",
      "Status": s.status === "graded" ? "Graded" : "Pending",
      "Submitted At": s.submitted_at ? new Date(s.submitted_at).toLocaleString() : ""
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Results");
    
    // Auto-width columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({ wch: Math.max(key.length, 18) }));
    ws["!cols"] = colWidths;
    
    XLSX.writeFile(wb, `${filename}.xlsx`);
    toast.success("Excel file downloaded!");
  };

  // Export Block Stats to Excel
  const exportBlockStatsToExcel = () => {
    const exportData = blockStats.map((s, index) => ({
      "Rank": index + 1,
      "Block": s.block,
      "State": s.state,
      "Total Candidates": s.count,
      "Average Score": s.avgScore,
      "Highest Score": s.maxScore,
      "Lowest Score": s.minScore,
      "Graded": s.graded
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Block Analysis");
    XLSX.writeFile(wb, "block_analysis.xlsx");
    toast.success("Block analysis Excel downloaded!");
  };

  // Export to PDF
  const exportToPDF = (data, filename) => {
    const doc = new jsPDF('landscape');
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(0, 100, 0);
    doc.text("Wangjing Women and Girls Society (WWAGS)", 14, 20);
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text("CRP-EP Examination Results", 14, 28);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);
    
    // Table with section-wise scores
    const tableData = data.map((s, index) => [
      index + 1,
      s.candidate_name,
      s.registration_number,
      s.block || "-",
      `${s.section1_score || 0}/${s.section1_total || 50}`,
      `${s.section2_score || 0}/${s.section2_total || 40}`,
      s.section2_qualified ? "Yes" : "No",
      `${s.subjective_score}/${s.subjective_total || 10}`,
      `${s.total_score}/${s.max_score || 100}`,
      s.qualified ? "QUALIFIED" : "NOT QUAL."
    ]);

    autoTable(doc, {
      startY: 42,
      head: [["#", "Name", "Reg. No", "Block", "Sec I", "Sec II", "Math Q", "Sec III", "Total", "Result"]],
      body: tableData,
      theme: "striped",
      headStyles: { fillColor: [0, 128, 0], fontSize: 8 },
      styles: { fontSize: 8 },
      columnStyles: {
        9: { fontStyle: 'bold' }
      }
    });

    doc.save(`${filename}.pdf`);
    toast.success("PDF file downloaded!");
  };

  // Export Block Stats to PDF
  const exportBlockStatsToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.setTextColor(0, 100, 0);
    doc.text("Block-wise Analysis Report", 14, 20);
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text("CRP-EP Examination", 14, 28);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);
    
    const tableData = blockStats.map((s, index) => [
      index + 1,
      s.block,
      s.state,
      s.count,
      s.avgScore,
      s.maxScore,
      s.minScore
    ]);

    autoTable(doc, {
      startY: 42,
      head: [["Rank", "Block", "State", "Candidates", "Avg Score", "Highest", "Lowest"]],
      body: tableData,
      theme: "striped",
      headStyles: { fillColor: [0, 128, 0] }
    });

    doc.save("block_analysis.pdf");
    toast.success("Block analysis PDF downloaded!");
  };

  // Download block-specific backup (Excel)
  const downloadBlockBackup = (blockName) => {
    const blockSubmissions = submissions.filter(s => s.block === blockName);
    if (blockSubmissions.length === 0) {
      toast.error(`No submissions found for ${blockName}`);
      return;
    }
    exportToExcel(blockSubmissions, `${blockName.replace(/\s+/g, '_')}_backup`);
    toast.success(`Downloaded ${blockSubmissions.length} records for ${blockName}`);
  };

  // Download Full Q&A PDF for a block - consolidated PDF with all candidates' questions and answers
  const downloadBlockFullQnAPDF = async (blockName) => {
    const blockSubmissions = submissions.filter(s => s.block === blockName);
    if (blockSubmissions.length === 0) {
      toast.error(`No submissions found for ${blockName}`);
      return;
    }

    setDownloadingQnA(blockName);
    setQnaProgress({ current: 0, total: blockSubmissions.length });
    toast.info(`Generating Full Q&A PDF for ${blockSubmissions.length} candidates in ${blockName}...`);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      let isFirstPage = true;

      // Helper to find question text from questions_data
      const getQuestionText = (questionsData, questionId) => {
        if (!questionsData) return null;
        for (const section of Object.values(questionsData)) {
          const q = section.questions?.find(q => q.id === questionId);
          if (q) return q;
        }
        return null;
      };

      // Helper to add new page if needed
      const checkPageBreak = (currentY, neededSpace = 20) => {
        if (currentY + neededSpace > pageHeight - margin) {
          doc.addPage();
          return margin + 5;
        }
        return currentY;
      };

      // Fetch detailed submissions one by one to avoid overwhelming the backend
      for (let i = 0; i < blockSubmissions.length; i++) {
        const sub = blockSubmissions[i];
        setQnaProgress({ current: i + 1, total: blockSubmissions.length });

        let detailed = null;
        try {
          const res = await axios.get(`${API}/admin/submission/${sub.id}`);
          detailed = res.data;
        } catch (err) {
          console.error(`Failed to fetch details for ${sub.candidate_name}`, err);
          continue;
        }

        if (!isFirstPage) doc.addPage();
        isFirstPage = false;

        let y = margin + 5;

        // Header
        doc.setFontSize(14);
        doc.setTextColor(4, 120, 87);
        doc.text("WWAGS - CRP-EP Examination", margin, y);
        y += 6;
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Block: ${blockName} | Generated: ${new Date().toLocaleString()}`, margin, y);
        y += 4;
        doc.setDrawColor(200);
        doc.line(margin, y, pageWidth - margin, y);
        y += 6;

        // Candidate Info Block (with photo if available)
        const photo = detailed.photo || sub.photo;
        const infoStartY = y;
        if (photo && typeof photo === "string" && photo.startsWith("data:image")) {
          try {
            doc.addImage(photo, "JPEG", pageWidth - margin - 25, y, 25, 30);
          } catch (e) {
            // Photo failed to embed; skip
          }
        }

        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.setFont(undefined, "bold");
        doc.text(`${i + 1}. ${detailed.candidate_name || sub.candidate_name}`, margin, y);
        y += 6;
        doc.setFont(undefined, "normal");
        doc.setFontSize(9);
        doc.text(`Reg. No: ${detailed.registration_number || sub.registration_number}`, margin, y);
        y += 4;
        doc.text(`State: ${detailed.state || "-"} | District: ${detailed.district || "-"}`, margin, y);
        y += 4;
        doc.text(`Block: ${detailed.block || "-"} | GP: ${detailed.gram_panchayat || "-"}`, margin, y);
        y += 4;
        if (detailed.mobile_number) {
          doc.text(`Mobile: ${detailed.mobile_number}`, margin, y);
          y += 4;
        }
        doc.text(`Submitted: ${detailed.submitted_at ? new Date(detailed.submitted_at).toLocaleString() : "N/A"}`, margin, y);
        y += 6;

        // Ensure we are below the photo if it was taller
        if (photo && y < infoStartY + 32) y = infoStartY + 32;

        // Score Summary
        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text(
          `Scores: Sec I: ${detailed.section1_score || 0}/${detailed.section1_total || 50} | ` +
          `Sec II: ${detailed.section2_score || 0}/${detailed.section2_total || 40} | ` +
          `Sec III: ${detailed.subjective_score || 0}/${detailed.subjective_total || 10} | ` +
          `Total: ${detailed.total_score || 0}/${detailed.max_score || 100}`,
          margin, y
        );
        y += 5;
        doc.setTextColor(detailed.qualified ? 16 : 220, detailed.qualified ? 185 : 38, detailed.qualified ? 129 : 38);
        doc.text(`Result: ${detailed.qualified ? "QUALIFIED" : "NOT QUALIFIED"}`, margin, y);
        doc.setTextColor(0);
        doc.setFont(undefined, "normal");
        y += 6;

        // Section I & II: MCQ Q&A Table
        const mcqRows = [];
        (detailed.mcq_results || []).forEach((r, idx) => {
          const question = getQuestionText(detailed.questions_data, r.question_id);
          const qText = question?.text || "[Question not found]";
          const truncated = qText.length > 85 ? qText.substring(0, 82) + "..." : qText;
          mcqRows.push([
            idx + 1,
            truncated,
            r.given_answer || "-",
            r.correct_answer || "-",
            r.is_correct ? "✓" : "✗"
          ]);
        });

        if (mcqRows.length > 0) {
          y = checkPageBreak(y, 20);
          doc.setFontSize(11);
          doc.setFont(undefined, "bold");
          doc.setTextColor(4, 120, 87);
          doc.text(`MCQ Answers (${mcqRows.filter(r => r[4] === "✓").length}/${mcqRows.length} correct)`, margin, y);
          doc.setTextColor(0);
          doc.setFont(undefined, "normal");
          y += 3;

          autoTable(doc, {
            startY: y,
            head: [["#", "Question", "Given", "Correct", "OK"]],
            body: mcqRows,
            theme: "grid",
            headStyles: { fillColor: [4, 120, 87], fontSize: 8 },
            styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
            columnStyles: {
              0: { cellWidth: 8 },
              1: { cellWidth: 135 },
              2: { cellWidth: 15, halign: "center" },
              3: { cellWidth: 15, halign: "center" },
              4: { cellWidth: 10, halign: "center" }
            },
            margin: { left: margin, right: margin }
          });
          y = doc.lastAutoTable.finalY + 6;
        }

        // Section III: Subjective Answers
        const subjAnswers = detailed.subjective_answers || [];
        if (subjAnswers.length > 0) {
          y = checkPageBreak(y, 20);
          doc.setFontSize(11);
          doc.setFont(undefined, "bold");
          doc.setTextColor(4, 120, 87);
          doc.text("Subjective Answers (Section III)", margin, y);
          doc.setTextColor(0);
          y += 5;

          subjAnswers.forEach((ans, idx) => {
            const question = getQuestionText(detailed.questions_data, ans.question_id);
            const qText = ans.selected_option === 1
              ? (question?.text || "[Question not found]")
              : (question?.alt_text || question?.text || "[Question not found]");
            const grade = (detailed.subjective_grades || []).find(g => g.question_id === ans.question_id);

            y = checkPageBreak(y, 30);

            doc.setFontSize(9);
            doc.setFont(undefined, "bold");
            const qLines = doc.splitTextToSize(`Q${idx + 1}: ${qText}`, pageWidth - 2 * margin);
            doc.text(qLines, margin, y);
            y += qLines.length * 4;

            doc.setFont(undefined, "normal");
            doc.setFontSize(8);
            doc.setTextColor(60);
            const answerText = ans.answer || "[No answer provided]";
            const aLines = doc.splitTextToSize(`Answer: ${answerText}`, pageWidth - 2 * margin);
            y = checkPageBreak(y, aLines.length * 3.5 + 5);
            doc.text(aLines, margin, y);
            y += aLines.length * 3.5 + 1;

            if (grade) {
              doc.setTextColor(4, 120, 87);
              doc.text(`Marks: ${grade.marks || 0}/5${grade.feedback ? " | Feedback: " + grade.feedback : ""}`, margin, y);
              y += 4;
            }
            doc.setTextColor(0);
            y += 2;
          });
        }
      }

      // Page numbers
      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${p} of ${totalPages}`, pageWidth - margin - 20, pageHeight - 8);
      }

      const safeBlock = blockName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      const dateStr = new Date().toISOString().split('T')[0];
      doc.save(`${safeBlock}_Full_QnA_${dateStr}.pdf`);
      toast.success(`Full Q&A PDF downloaded: ${blockSubmissions.length} candidates from ${blockName}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate Full Q&A PDF");
    } finally {
      setDownloadingQnA(null);
      setQnaProgress({ current: 0, total: 0 });
    }
  };

  // Download block-specific backup (PDF)
  const downloadBlockPDF = (blockName) => {
    const blockSubmissions = submissions.filter(s => s.block === blockName);
    if (blockSubmissions.length === 0) {
      toast.error(`No submissions found for ${blockName}`);
      return;
    }
    exportToPDF(blockSubmissions, `${blockName.replace(/\s+/g, '_')}_backup`);
  };

  // Download all qualified candidates
  const downloadQualifiedBackup = () => {
    const qualified = submissions.filter(s => s.qualified === true);
    if (qualified.length === 0) {
      toast.error("No qualified candidates found");
      return;
    }
    exportToExcel(qualified, "qualified_candidates_backup");
    toast.success(`Downloaded ${qualified.length} qualified candidates`);
  };

  // Download all not-qualified candidates
  const downloadNotQualifiedBackup = () => {
    const notQualified = submissions.filter(s => s.qualified !== true);
    if (notQualified.length === 0) {
      toast.error("No not-qualified candidates found");
      return;
    }
    exportToExcel(notQualified, "not_qualified_candidates_backup");
    toast.success(`Downloaded ${notQualified.length} not-qualified candidates`);
  };

  // Print Rankings
  const handlePrintRankings = () => {
    const printContent = `
      <html>
      <head>
        <title>CRP-EP Examination Rankings</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #047857; margin-bottom: 5px; }
          h2 { color: #666; font-weight: normal; margin-top: 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background-color: #047857; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .rank-1 { background-color: #fef3c7 !important; }
          .rank-2 { background-color: #f3f4f6 !important; }
          .rank-3 { background-color: #fed7aa !important; }
          .header-info { margin-bottom: 20px; color: #666; }
          .qualified { color: #059669; font-weight: bold; }
          .not-qualified { color: #dc2626; font-weight: bold; }
          @page { size: landscape; }
        </style>
      </head>
      <body>
        <h1>Wangjing Women and Girls Society (WWAGS)</h1>
        <h2>CRP-EP Examination Rankings</h2>
        <p class="header-info">Generated on: ${new Date().toLocaleString()} | Total Candidates: ${filteredSubmissions.length}</p>
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Candidate Name</th>
              <th>Reg. No</th>
              <th>Block</th>
              <th>Sec I (GK)</th>
              <th>Sec II (Math)</th>
              <th>Math Qual.</th>
              <th>Sec III</th>
              <th>Total</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            ${filteredSubmissions.map((s, i) => `
              <tr class="${i < 3 ? `rank-${i+1}` : ''}">
                <td>${i + 1}</td>
                <td>${s.candidate_name}</td>
                <td>${s.registration_number}</td>
                <td>${s.block || '-'}</td>
                <td>${s.section1_score || 0}/${s.section1_total || 50}</td>
                <td>${s.section2_score || 0}/${s.section2_total || 40}</td>
                <td class="${s.section2_qualified ? 'qualified' : 'not-qualified'}">${s.section2_qualified ? 'Yes' : 'No'}</td>
                <td>${s.subjective_score}/${s.subjective_total || 10}</td>
                <td><strong>${s.total_score}/${s.max_score || 100}</strong></td>
                <td class="${s.qualified ? 'qualified' : 'not-qualified'}">${s.qualified ? 'QUALIFIED' : 'NOT QUAL.'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Print Detailed Scorecard with Q&A
  const handlePrintScorecard = () => {
    if (!detailedSubmission) return;
    
    const getQuestionText = (questionId) => {
      if (!detailedSubmission?.questions_data) return null;
      for (const section of Object.values(detailedSubmission.questions_data)) {
        const q = section.questions?.find(q => q.id === questionId);
        if (q) return q;
      }
      return null;
    };

    const mcqContent = detailedSubmission.mcq_results?.map((result, index) => {
      const question = getQuestionText(result.question_id);
      return `
        <div class="question ${result.is_correct ? 'correct' : 'incorrect'}">
          <div class="q-header">
            <span class="q-num">Q${index + 1}</span>
            <span class="q-status">${result.is_correct ? '✓ Correct' : '✗ Incorrect'}</span>
          </div>
          <p class="q-text">${question?.text || 'Question not found'}</p>
          <div class="q-options">
            ${question?.options?.map((opt, i) => `
              <div class="option ${['A','B','C','D'][i] === result.given_answer ? 'selected' : ''} ${['A','B','C','D'][i] === result.correct_answer ? 'correct-answer' : ''}">
                <strong>${['A','B','C','D'][i]}.</strong> ${opt}
              </div>
            `).join('') || ''}
          </div>
          <p class="answer-info">Given: <strong>${result.given_answer}</strong> | Correct: <strong>${result.correct_answer}</strong></p>
        </div>
      `;
    }).join('') || '';

    const subjectiveContent = detailedSubmission.subjective_answers?.map((answer) => {
      const question = getQuestionText(answer.question_id);
      const selectedText = answer.selected_option === 1 ? question?.text : question?.alt_text;
      const grade = detailedSubmission.subjective_grades?.find(g => g.question_id === answer.question_id);
      return `
        <div class="subjective-question">
          <div class="q-header">
            <span class="q-num">Q${answer.question_id}</span>
            <span class="marks">Marks: ${grade?.marks || 0}/5</span>
          </div>
          <p class="q-text">${selectedText || 'Question not found'}</p>
          <div class="answer-box">
            <strong>Candidate's Answer:</strong>
            <p>${answer.answer}</p>
          </div>
          ${grade?.feedback ? `<p class="feedback"><strong>Feedback:</strong> ${grade.feedback}</p>` : ''}
        </div>
      `;
    }).join('') || '';

    const printContent = `
      <html>
      <head>
        <title>Scorecard - ${detailedSubmission.candidate_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { color: #047857; margin-bottom: 5px; font-size: 22px; }
          h2 { color: #666; font-weight: normal; margin-top: 0; font-size: 16px; }
          .candidate-info { background: #f9f9f9; padding: 15px; border-radius: 8px; margin: 20px 0; }
          .candidate-info p { margin: 5px 0; }
          .score-summary { display: flex; gap: 20px; margin: 20px 0; }
          .score-box { flex: 1; text-align: center; padding: 15px; border-radius: 8px; }
          .score-box.mcq { background: #d1fae5; }
          .score-box.subjective { background: #dbeafe; }
          .score-box.total { background: #fef3c7; }
          .score-box h3 { margin: 0 0 5px 0; font-size: 14px; color: #666; }
          .score-box .score { font-size: 28px; font-weight: bold; }
          .section-title { background: #047857; color: white; padding: 10px 15px; margin: 30px 0 15px 0; }
          .question { border: 1px solid #ddd; padding: 15px; margin-bottom: 15px; border-radius: 8px; }
          .question.correct { border-left: 4px solid #10b981; background: #f0fdf4; }
          .question.incorrect { border-left: 4px solid #ef4444; background: #fef2f2; }
          .q-header { display: flex; justify-content: space-between; margin-bottom: 10px; }
          .q-num { font-weight: bold; color: #047857; }
          .q-status { font-size: 12px; }
          .q-text { margin: 10px 0; }
          .q-options { margin: 10px 0; }
          .option { padding: 5px 10px; margin: 3px 0; background: #f9f9f9; border-radius: 4px; }
          .option.selected { background: #fee2e2; }
          .option.correct-answer { background: #d1fae5; }
          .answer-info { font-size: 12px; color: #666; margin-top: 10px; }
          .subjective-question { border: 1px solid #ddd; padding: 15px; margin-bottom: 15px; border-radius: 8px; }
          .answer-box { background: #f9f9f9; padding: 15px; margin: 10px 0; border-radius: 4px; }
          .answer-box p { white-space: pre-wrap; }
          .marks { background: #047857; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px; }
          .feedback { background: #fef3c7; padding: 10px; border-radius: 4px; margin-top: 10px; }
          @media print { .page-break { page-break-before: always; } }
        </style>
      </head>
      <body>
        <h1>Wangjing Women and Girls Society (WWAGS)</h1>
        <h2>CRP-EP Examination - Detailed Scorecard</h2>
        
        <div class="candidate-info">
          <p><strong>Name:</strong> ${detailedSubmission.candidate_name}</p>
          <p><strong>Registration No:</strong> ${detailedSubmission.registration_number}</p>
          <p><strong>State:</strong> ${detailedSubmission.state || 'N/A'} | <strong>District:</strong> ${detailedSubmission.district || 'N/A'} | <strong>Block:</strong> ${detailedSubmission.block || 'N/A'}</p>
          <p><strong>Submitted:</strong> ${detailedSubmission.submitted_at ? new Date(detailedSubmission.submitted_at).toLocaleString() : 'N/A'}</p>
        </div>

        <div class="score-summary">
          <div class="score-box mcq">
            <h3>MCQ Score</h3>
            <div class="score">${detailedSubmission.mcq_score}/${detailedSubmission.mcq_total || 90}</div>
          </div>
          <div class="score-box subjective">
            <h3>Subjective Score</h3>
            <div class="score">${detailedSubmission.subjective_score}/${detailedSubmission.subjective_total || 10}</div>
          </div>
          <div class="score-box total">
            <h3>Total Score</h3>
            <div class="score">${detailedSubmission.total_score}/${detailedSubmission.max_score || 100}</div>
          </div>
        </div>

        <div class="section-title">Section I & II: MCQ Answers (${detailedSubmission.mcq_results?.filter(r => r.is_correct).length || 0}/${detailedSubmission.mcq_results?.length || 0} Correct)</div>
        ${mcqContent}

        <div class="page-break"></div>
        <div class="section-title">Section III: Subjective Answers</div>
        ${subjectiveContent}
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const getRankBadge = (index) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (index === 1) return <Medal className="w-5 h-5 text-gray-400" />;
    if (index === 2) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-sm font-medium text-gray-500">#{index + 1}</span>;
  };

  const getScoreColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return "text-emerald-600";
    if (percentage >= 60) return "text-blue-600";
    if (percentage >= 40) return "text-amber-600";
    return "text-red-600";
  };

  // Simple bar chart component
  const BarChart = ({ data, maxValue }) => {
    const chartMax = maxValue || Math.max(...data.map(d => parseFloat(d.avgScore)));
    return (
      <div className="space-y-3">
        {data.slice(0, 10).map((item, index) => (
          <div key={item.block} className="flex items-center gap-3">
            <div className="w-32 text-sm font-medium truncate" title={item.block}>
              {item.block}
            </div>
            <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden relative">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg transition-all duration-500"
                style={{ width: `${(parseFloat(item.avgScore) / chartMax) * 100}%` }}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-700">
                {item.avgScore} avg
              </span>
            </div>
            <div className="w-16 text-right text-sm text-gray-500">
              {item.count} candidates
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Download All Records for Backup
  const handleDownloadAllRecords = async () => {
    setDownloadingRecords(true);
    try {
      const response = await axios.get(`${API}/admin/download-all-records`);
      const data = response.data;
      
      // Create comprehensive Excel workbook
      const wb = XLSX.utils.book_new();
      
      // Sheet 1: Summary
      const summaryData = data.exam_records.map((record, index) => ({
        "S.No": index + 1,
        "Candidate Name": record.candidate_info.name,
        "Registration No": record.candidate_info.registration_number,
        "State": record.candidate_info.state,
        "District": record.candidate_info.district,
        "Block": record.candidate_info.block,
        "Gram Panchayat": record.candidate_info.gram_panchayat,
        "Section I Score": `${record.scores.section1_score}/${record.scores.section1_total}`,
        "Section II Score": `${record.scores.section2_score}/${record.scores.section2_total}`,
        "Section II Qualified": record.scores.section2_qualified ? "Yes" : "No",
        "Section III Score": `${record.scores.subjective_score}/${record.scores.subjective_total}`,
        "Total Score": `${record.scores.total_score}/${record.scores.max_score}`,
        "Overall Qualified": record.scores.qualified ? "QUALIFIED" : "NOT QUALIFIED",
        "Status": record.exam_info.status,
        "Submitted At": record.exam_info.submitted_at,
        "Graded At": record.exam_info.graded_at
      }));
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWs, "Results Summary");
      
      // Sheet 2: All Candidates
      const candidatesData = data.all_candidates.map((c, index) => ({
        "S.No": index + 1,
        "Candidate ID": c.id,
        "Name": c.name,
        "Registration No": c.registration_number,
        "State": c.state,
        "District": c.district,
        "Block": c.block,
        "Gram Panchayat": c.gram_panchayat || "",
        "Approved": c.approved ? "Yes" : "No",
        "Logged In": c.logged_in ? "Yes" : "No",
        "Registered At": c.registered_at,
        "Question Set": c.assigned_question_set || "Default"
      }));
      const candidatesWs = XLSX.utils.json_to_sheet(candidatesData);
      XLSX.utils.book_append_sheet(wb, candidatesWs, "All Candidates");
      
      // Sheet 3: Detailed Answers
      const answersData = [];
      data.exam_records.forEach((record) => {
        record.mcq_answers?.forEach((ans, idx) => {
          answersData.push({
            "Candidate Name": record.candidate_info.name,
            "Registration No": record.candidate_info.registration_number,
            "Question Type": "MCQ",
            "Question ID": ans.question_id,
            "Given Answer": ans.answer,
            "Is Correct": ans.is_correct ? "Yes" : "No",
            "Marks Obtained": ans.is_correct ? 2 : 0
          });
        });
        record.subjective_answers?.forEach((ans) => {
          answersData.push({
            "Candidate Name": record.candidate_info.name,
            "Registration No": record.candidate_info.registration_number,
            "Question Type": "Subjective",
            "Question ID": ans.question_id,
            "Given Answer": ans.answer,
            "Is Correct": "-",
            "Marks Obtained": "-"
          });
        });
      });
      if (answersData.length > 0) {
        const answersWs = XLSX.utils.json_to_sheet(answersData);
        XLSX.utils.book_append_sheet(wb, answersWs, "All Answers");
      }
      
      // Sheet 4: Question Bank
      const questionBankData = [];
      ["section1", "section2", "section3"].forEach((section) => {
        data.question_bank?.[section]?.questions?.forEach((q) => {
          questionBankData.push({
            "Section": section.toUpperCase(),
            "Question ID": q.id,
            "Question Text": q.text,
            "Question (Translation)": q.text_as || "",
            "Option A": q.options?.[0] || "",
            "Option B": q.options?.[1] || "",
            "Option C": q.options?.[2] || "",
            "Option D": q.options?.[3] || "",
            "Correct Answer": q.correct || "",
            "Hidden": q.hidden ? "Yes" : "No"
          });
        });
      });
      if (questionBankData.length > 0) {
        const questionBankWs = XLSX.utils.json_to_sheet(questionBankData);
        XLSX.utils.book_append_sheet(wb, questionBankWs, "Question Bank");
      }
      
      // Generate filename with date
      const dateStr = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `CRP_EP_Full_Backup_${dateStr}.xlsx`);
      
      toast.success(`Downloaded ${data.total_records} exam records with all details`);
    } catch (error) {
      toast.error("Failed to download records");
      console.error(error);
    } finally {
      setDownloadingRecords(false);
    }
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
            <p className="text-sm text-gray-500">Block-wise analysis and rankings</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportToExcel(filteredSubmissions, "crp_ep_results")}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportToPDF(filteredSubmissions, "crp_ep_results")}
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrintRankings}
              data-testid="print-rankings-btn"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadQualifiedBackup}
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              data-testid="download-qualified-btn"
            >
              <Award className="w-4 h-4 mr-2" /> Qualified ({qualifiedCount})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadAllRecords}
              disabled={downloadingRecords}
              className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
              data-testid="download-all-records-btn"
            >
              <Archive className="w-4 h-4 mr-2" /> 
              {downloadingRecords ? "Downloading..." : "Backup All"}
            </Button>
          </div>
        </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 sm:pb-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Candidates</p>
                  <p className="text-2xl font-bold">{overallStats.total}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Average Score</p>
                  <p className="text-2xl font-bold">{overallStats.avgScore}/100</p>
                </div>
                <BarChart3 className="w-8 h-8 text-emerald-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Top Score</p>
                  <p className="text-2xl font-bold">{overallStats.topScore}/100</p>
                </div>
                <Trophy className="w-8 h-8 text-amber-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Graded</p>
                  <p className="text-2xl font-bold">{overallStats.graded}/{overallStats.total}</p>
                </div>
                <Award className="w-8 h-8 text-purple-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="rankings" className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-lg">
            <TabsTrigger value="rankings" data-testid="rankings-tab">
              <TrendingUp className="w-4 h-4 mr-2" /> Rankings
            </TabsTrigger>
            <TabsTrigger value="blockwise" data-testid="blockwise-tab">
              <BarChart3 className="w-4 h-4 mr-2" /> Block Analysis
            </TabsTrigger>
            <TabsTrigger value="search" data-testid="search-tab">
              <Search className="w-4 h-4 mr-2" /> Search
            </TabsTrigger>
          </TabsList>

          {/* Rankings Tab */}
          <TabsContent value="rankings">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Candidate Rankings
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
                      data-testid="toggle-sort-btn"
                    >
                      {sortOrder === "desc" ? (
                        <><ChevronDown className="w-4 h-4 mr-1" /> High to Low</>
                      ) : (
                        <><ChevronUp className="w-4 h-4 mr-1" /> Low to High</>
                      )}
                    </Button>
                  </div>
                </div>
                {/* Qualification Filter Tabs */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant={qualificationFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQualificationFilter("all")}
                    className={qualificationFilter === "all" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    data-testid="filter-all-btn"
                  >
                    All ({submissions.length})
                  </Button>
                  <Button
                    variant={qualificationFilter === "qualified" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQualificationFilter("qualified")}
                    className={qualificationFilter === "qualified" ? "bg-green-600 hover:bg-green-700" : "text-green-600 border-green-200 hover:bg-green-50"}
                    data-testid="filter-qualified-btn"
                  >
                    <Award className="w-4 h-4 mr-1" />
                    Qualified ({qualifiedCount})
                  </Button>
                  <Button
                    variant={qualificationFilter === "not_qualified" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setQualificationFilter("not_qualified")}
                    className={qualificationFilter === "not_qualified" ? "bg-red-600 hover:bg-red-700" : "text-red-600 border-red-200 hover:bg-red-50"}
                    data-testid="filter-not-qualified-btn"
                  >
                    Not Qualified ({notQualifiedCount})
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Rank</TableHead>
                        <TableHead>Candidate Name</TableHead>
                        <TableHead>Reg. Number</TableHead>
                        <TableHead>Block</TableHead>
                        <TableHead>Sec I</TableHead>
                        <TableHead>Sec II</TableHead>
                        <TableHead>Sec III</TableHead>
                        <TableHead className="text-center">Total</TableHead>
                        <TableHead className="text-center">Result</TableHead>
                        <TableHead className="w-20">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSubmissions.map((submission, index) => (
                        <TableRow 
                          key={submission.id} 
                          className={index < 3 ? "bg-amber-50/50" : ""}
                          data-testid={`ranking-row-${index}`}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center justify-center w-8 h-8">
                              {getRankBadge(index)}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{submission.candidate_name}</TableCell>
                          <TableCell className="text-gray-600">{submission.registration_number}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-medium">{submission.block}</div>
                              <div className="text-gray-400 text-xs">{submission.state}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-emerald-700 text-sm">
                              {submission.section1_score || 0}/{submission.section1_total || 50}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-amber-700 text-sm">
                                {submission.section2_score || 0}/{submission.section2_total || 40}
                              </span>
                              {submission.section2_qualified === false && (
                                <span className="text-xs text-red-500">Not Qual.</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-teal-700 text-sm">
                              {submission.subjective_score}/{submission.subjective_total || 10}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`text-lg font-bold ${getScoreColor(submission.total_score, submission.max_score || 100)}`}>
                              {submission.total_score}
                              <span className="text-xs text-gray-400">/{submission.max_score || 100}</span>
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge 
                              className={
                                submission.qualified 
                                  ? "bg-green-100 text-green-800" 
                                  : "bg-red-100 text-red-800"
                              }
                            >
                              {submission.qualified ? "QUALIFIED" : "NOT QUAL."}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchSubmissionDetails(submission.id)}
                              data-testid={`view-scorecard-${submission.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Block Analysis Tab */}
          <TabsContent value="blockwise">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Block Performance Chart */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-emerald-600" />
                      Block-wise Average Score (Top 10)
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={exportBlockStatsToExcel}
                        data-testid="export-block-excel-btn"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={exportBlockStatsToPDF}
                        data-testid="export-block-pdf-btn"
                      >
                        <FileText className="w-4 h-4 mr-2" /> PDF
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {blockStats.length > 0 ? (
                    <BarChart data={blockStats} maxValue={100} />
                  ) : (
                    <p className="text-gray-500 text-center py-8">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Block Statistics Table */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Detailed Block Statistics</CardTitle>
                  <p className="text-sm text-gray-500">Download per block: <FileSpreadsheet className="w-3 h-3 inline text-emerald-600" /> Excel summary, <FileText className="w-3 h-3 inline text-red-600" /> PDF summary, <BookOpen className="w-3 h-3 inline text-indigo-600" /> Full Q&A PDF (all candidates with every question and answer)</p>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Block</TableHead>
                          <TableHead>State</TableHead>
                          <TableHead className="text-center">Candidates</TableHead>
                          <TableHead className="text-center">Avg Score</TableHead>
                          <TableHead className="text-center">Highest</TableHead>
                          <TableHead className="text-center">Lowest</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {blockStats.map((stat, index) => (
                          <TableRow key={stat.block} data-testid={`block-stat-${index}`}>
                            <TableCell className="font-medium">{stat.block}</TableCell>
                            <TableCell className="text-gray-600">{stat.state}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">{stat.count}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`font-bold ${getScoreColor(parseFloat(stat.avgScore), 100)}`}>
                                {stat.avgScore}
                              </span>
                            </TableCell>
                            <TableCell className="text-center text-emerald-600 font-medium">
                              {stat.maxScore}
                            </TableCell>
                            <TableCell className="text-center text-amber-600 font-medium">
                              {stat.minScore}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => downloadBlockBackup(stat.block)}
                                  title="Download Excel"
                                  className="h-7 w-7 p-0"
                                  data-testid={`download-block-excel-${index}`}
                                >
                                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => downloadBlockPDF(stat.block)}
                                  title="Download Summary PDF"
                                  className="h-7 w-7 p-0"
                                  data-testid={`download-block-pdf-${index}`}
                                >
                                  <FileText className="w-4 h-4 text-red-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => downloadBlockFullQnAPDF(stat.block)}
                                  disabled={downloadingQnA !== null}
                                  title="Download Full Q&A PDF (all candidates, all questions & answers)"
                                  className="h-7 w-7 p-0"
                                  data-testid={`download-block-qna-pdf-${index}`}
                                >
                                  {downloadingQnA === stat.block ? (
                                    <div className="w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <BookOpen className="w-4 h-4 text-indigo-600" />
                                  )}
                                </Button>
                              </div>
                              {downloadingQnA === stat.block && (
                                <div className="text-xs text-indigo-600 mt-1">
                                  {qnaProgress.current}/{qnaProgress.total}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Search Tab */}
          <TabsContent value="search">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-blue-600" />
                  Search & Filter Candidates
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search Filters */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="search">Search by Name / Reg. No</Label>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="search"
                        placeholder="Enter name or registration..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="search-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Filter by State</Label>
                    <Select value={stateFilter} onValueChange={(val) => { setStateFilter(val); setBlockFilter("all"); }}>
                      <SelectTrigger className="mt-1" data-testid="state-filter">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All States</SelectItem>
                        {uniqueStates.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Filter by Block</Label>
                    <Select value={blockFilter} onValueChange={setBlockFilter}>
                      <SelectTrigger className="mt-1" data-testid="block-filter">
                        <SelectValue placeholder="All Blocks" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Blocks</SelectItem>
                        {uniqueBlocks.map(block => (
                          <SelectItem key={block} value={block}>{block}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      onClick={() => { setSearchTerm(""); setStateFilter("all"); setBlockFilter("all"); }}
                      className="w-full"
                      data-testid="clear-filters-btn"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>

                {/* Results count */}
                <div className="mb-4 text-sm text-gray-600">
                  Showing <span className="font-semibold">{filteredSubmissions.length}</span> of {submissions.length} candidates
                </div>

                {/* Results Table */}
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Candidate Name</TableHead>
                        <TableHead>Reg. Number</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>District</TableHead>
                        <TableHead>Block</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Total Score</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSubmissions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                            No candidates found matching your filters
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredSubmissions.map((submission) => (
                          <TableRow key={submission.id} data-testid={`search-result-${submission.id}`}>
                            <TableCell className="font-medium">{submission.candidate_name}</TableCell>
                            <TableCell>{submission.registration_number}</TableCell>
                            <TableCell>{submission.state}</TableCell>
                            <TableCell>{submission.district}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-emerald-50">
                                {submission.block}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                className={
                                  submission.status === "graded" 
                                    ? "bg-green-100 text-green-800" 
                                    : "bg-yellow-100 text-yellow-800"
                                }
                              >
                                {submission.status === "graded" ? "Graded" : "Pending"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-bold text-lg">
                                {submission.total_score}/{submission.max_score || 100}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => fetchSubmissionDetails(submission.id)}
                                data-testid={`view-${submission.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" /> View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Detailed Scorecard Dialog */}
      <Dialog open={showScorecard} onOpenChange={setShowScorecard}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-emerald-600" />
              Detailed Scorecard
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : detailedSubmission && (
            <div className="space-y-6">
              {/* Candidate Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-semibold">{detailedSubmission.candidate_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Registration No</p>
                    <p className="font-semibold">{detailedSubmission.registration_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Block</p>
                    <p className="font-semibold">{detailedSubmission.block || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* Score Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-gray-600">MCQ Score</p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {detailedSubmission.mcq_score}/{detailedSubmission.mcq_total || 90}
                  </p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Subjective Score</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {detailedSubmission.subjective_score}/{detailedSubmission.subjective_total || 10}
                  </p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Score</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {detailedSubmission.total_score}/{detailedSubmission.max_score || 100}
                  </p>
                </div>
              </div>

              {/* MCQ Summary */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-3">MCQ Performance</h3>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-emerald-600">
                    Correct: {detailedSubmission.mcq_results?.filter(r => r.is_correct).length || 0}
                  </span>
                  <span className="text-red-600">
                    Incorrect: {detailedSubmission.mcq_results?.filter(r => !r.is_correct).length || 0}
                  </span>
                  <span className="text-gray-600">
                    Total: {detailedSubmission.mcq_results?.length || 0}
                  </span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/submission/${detailedSubmission?.id}`)}
              data-testid="view-full-details-btn"
            >
              <Eye className="w-4 h-4 mr-2" /> Full Review
            </Button>
            <Button
              variant="outline"
              onClick={handlePrintScorecard}
              data-testid="print-scorecard-btn"
            >
              <Printer className="w-4 h-4 mr-2" /> Print with Q&A
            </Button>
            <Button
              onClick={() => setShowScorecard(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminSidebar>
  );
}
